import { useMemo, useState } from 'react';
import { collection, query, orderBy } from 'firebase/firestore';
import { motion, AnimatePresence } from 'framer-motion';
import { db } from '@/firebase/config';
import { useAuth } from '@/hooks/useAuth';
import { useCollection } from '@/firebase/firestore/use-collection';
import { sanitizeFirestoreData } from '@/lib/utils';
import type { Jarticard, JarticardSobre, UserJarticard } from '@/lib/types';
import { JARTICARD_TYPE_LABELS } from '@/lib/types';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { createMarketListing } from '@/lib/actions';
import { JarticardFrame } from './JarticardFrame';
import { SobreStore } from './SobreStore';
import { TradeMarket } from './TradeMarket';
import { BookOpen, Store, Repeat, ChevronLeft, ChevronRight, Loader2, Layers, Sparkles } from 'lucide-react';

const CARDS_PER_PAGE = 9;

export function JarticardsClient() {
  const { user, loading: authLoading } = useAuth();

  // ── Datos en tiempo real ──
  const cardsQuery = useMemo(() => query(collection(db, 'jarticards'), orderBy('number', 'asc')), []);
  const { data: rawCards } = useCollection<Jarticard>(cardsQuery);

  const sobresQuery = useMemo(() => collection(db, 'jarticard_sobres'), []);
  const { data: rawSobres } = useCollection<JarticardSobre>(sobresQuery);

  const ownedQuery = useMemo(
    () => (user ? collection(db, 'users', user.uid, 'jarticards') : null),
    [user],
  );
  const { data: rawOwned } = useCollection<UserJarticard>(ownedQuery);

  const purchasesQuery = useMemo(
    () => (user ? collection(db, 'users', user.uid, 'jarticard_purchases') : null),
    [user],
  );
  const { data: rawPurchases } = useCollection<{ count: number }>(purchasesQuery);

  const allCards = useMemo(
    () => (rawCards || []).map((c) => sanitizeFirestoreData(c) as Jarticard),
    [rawCards],
  );
  const sobres = useMemo(
    () => (rawSobres || []).map((s) => sanitizeFirestoreData(s) as JarticardSobre).filter((s) => s.active !== false),
    [rawSobres],
  );
  const ownedMap = useMemo(() => {
    const m = new Map<string, number>();
    (rawOwned || []).forEach((o) => m.set(o.id, o.count || 0));
    return m;
  }, [rawOwned]);

  const purchasesMap = useMemo(() => {
    const m = new Map<string, number>();
    (rawPurchases || []).forEach((p) => m.set(p.id, p.count || 0));
    return m;
  }, [rawPurchases]);

  const cardsById = useMemo(() => {
    const m = new Map<string, Jarticard>();
    allCards.forEach((c) => m.set(c.id, c));
    return m;
  }, [allCards]);

  if (authLoading) return null;

  return (
    <div className="space-y-6 pb-12">
      {/* Hero */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45 }}
        className="relative overflow-hidden rounded-3xl border border-white/10 p-7 md:p-10"
        style={{ background: 'radial-gradient(120% 140% at 0% 0%, rgba(245,158,11,0.18), transparent 50%), radial-gradient(120% 140% at 100% 100%, rgba(217,70,239,0.16), transparent 50%), linear-gradient(160deg, rgba(20,20,28,0.6), rgba(10,10,14,0.4))' }}
      >
        {/* Conic glow girando */}
        <motion.div
          className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full opacity-30 blur-2xl"
          animate={{ rotate: 360 }}
          transition={{ repeat: Infinity, duration: 18, ease: 'linear' }}
          style={{ background: 'conic-gradient(from 0deg, #f59e0b, #ec4899, #a855f7, #f59e0b)' }}
        />
        {/* Siluetas de cartas flotando */}
        {[
          { l: '78%', t: '12%', r: 12, d: 0 },
          { l: '88%', t: '52%', r: -10, d: 1 },
          { l: '68%', t: '64%', r: 6, d: 0.5 },
        ].map((c, i) => (
          <motion.div
            key={i}
            className="pointer-events-none absolute hidden aspect-[5/7] w-16 rounded-lg border border-white/15 bg-white/5 backdrop-blur-sm md:block"
            style={{ left: c.l, top: c.t, rotate: `${c.r}deg` }}
            animate={{ y: [0, -10, 0] }}
            transition={{ repeat: Infinity, duration: 4, delay: c.d, ease: 'easeInOut' }}
          />
        ))}

        <div className="relative flex items-center gap-5">
          <motion.div
            className="relative"
            animate={{ y: [0, -5, 0] }}
            transition={{ repeat: Infinity, duration: 3, ease: 'easeInOut' }}
          >
            <div className="absolute inset-0 rounded-2xl bg-amber-500/50 blur-xl" />
            <div className="relative grid h-16 w-16 place-items-center rounded-2xl border border-amber-400/40 bg-amber-500/15 text-amber-300 shadow-[0_0_30px_rgba(245,158,11,0.4)]">
              <Layers className="h-9 w-9" />
            </div>
          </motion.div>
          <div>
            <h1
              className="font-headline text-4xl font-black md:text-6xl"
              style={{
                background: 'linear-gradient(90deg, #fde68a, #f59e0b, #ec4899, #a855f7, #fde68a)',
                backgroundSize: '300% 100%',
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                color: 'transparent',
                animation: 'jc-rainbow 6s linear infinite',
              }}
            >
              JartiCards
            </h1>
            <p className="mt-2 max-w-2xl text-muted-foreground">
              Colecciona, abre sobres e intercambia las cartas de la comunidad cofrade.
            </p>
          </div>
        </div>
      </motion.div>

      <Tabs defaultValue="album" className="w-full">
        <TabsList className="grid h-12 w-full max-w-lg grid-cols-3 gap-1 rounded-2xl border border-white/10 bg-card/40 p-1.5 backdrop-blur">
          <TabsTrigger value="album" className="gap-1.5 rounded-xl data-[state=active]:bg-gradient-to-br data-[state=active]:from-amber-500 data-[state=active]:to-amber-600 data-[state=active]:text-white data-[state=active]:shadow-lg"><BookOpen className="h-4 w-4" /> Álbum</TabsTrigger>
          <TabsTrigger value="tienda" className="gap-1.5 rounded-xl data-[state=active]:bg-gradient-to-br data-[state=active]:from-fuchsia-500 data-[state=active]:to-purple-600 data-[state=active]:text-white data-[state=active]:shadow-lg"><Store className="h-4 w-4" /> Tienda</TabsTrigger>
          <TabsTrigger value="mercado" className="gap-1.5 rounded-xl data-[state=active]:bg-gradient-to-br data-[state=active]:from-sky-500 data-[state=active]:to-blue-600 data-[state=active]:text-white data-[state=active]:shadow-lg"><Repeat className="h-4 w-4" /> Mercado</TabsTrigger>
        </TabsList>

        <TabsContent value="album" className="mt-6">
          <Album allCards={allCards} ownedMap={ownedMap} />
        </TabsContent>

        <TabsContent value="tienda" className="mt-6">
          <SobreStore sobres={sobres} cardsById={cardsById} purchases={purchasesMap} />
        </TabsContent>

        <TabsContent value="mercado" className="mt-6">
          <TradeMarket cardsById={cardsById} ownedMap={ownedMap} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ── Álbum estilo libro (9 cartas por hoja) ──

function Album({ allCards, ownedMap }: { allCards: Jarticard[]; ownedMap: Map<string, number> }) {
  const { toast } = useToast();
  const [page, setPage] = useState(0);
  const [dir, setDir] = useState(1);
  const [selected, setSelected] = useState<Jarticard | null>(null);
  const [listing, setListing] = useState(false);

  const totalPages = Math.max(1, Math.ceil(allCards.length / CARDS_PER_PAGE));
  const safePage = Math.min(page, totalPages - 1);
  const pageCards = allCards.slice(safePage * CARDS_PER_PAGE, safePage * CARDS_PER_PAGE + CARDS_PER_PAGE);

  const uniqueOwned = allCards.filter((c) => (ownedMap.get(c.id) || 0) > 0).length;
  const progress = allCards.length ? Math.round((uniqueOwned / allCards.length) * 100) : 0;

  const go = (delta: number) => {
    setDir(delta);
    setPage((p) => Math.max(0, Math.min(totalPages - 1, p + delta)));
  };

  const handleList = async (card: Jarticard) => {
    setListing(true);
    const res = await createMarketListing(card.id);
    setListing(false);
    toast({
      title: res.success ? 'Publicada' : 'Error',
      description: res.message,
      variant: res.success ? undefined : 'destructive',
    });
    if (res.success) setSelected(null);
  };

  if (allCards.length === 0) {
    return (
      <div className="grid place-items-center rounded-2xl border border-dashed border-white/10 py-20 text-center text-muted-foreground">
        <Sparkles className="mb-3 h-10 w-10 opacity-40" />
        Todavía no hay cartas en la colección. ¡Vuelve pronto!
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Progreso */}
      <div className="flex items-center gap-3">
        <div className="h-2 flex-1 overflow-hidden rounded-full bg-white/10">
          <div className="h-full rounded-full bg-gradient-to-r from-amber-400 to-amber-600 transition-all" style={{ width: `${progress}%` }} />
        </div>
        <span className="shrink-0 text-sm font-semibold text-muted-foreground">
          {uniqueOwned}/{allCards.length} ({progress}%)
        </span>
      </div>

      {/* Libro */}
      <div className="relative mx-auto max-w-3xl [perspective:1800px]">
        <div className="rounded-2xl border border-amber-900/30 bg-gradient-to-br from-[#2a1c0c] to-[#1a1208] p-3 shadow-2xl sm:p-6">
          <AnimatePresence mode="wait" custom={dir}>
            <motion.div
              key={safePage}
              custom={dir}
              initial={{ rotateY: dir > 0 ? 70 : -70, opacity: 0 }}
              animate={{ rotateY: 0, opacity: 1 }}
              exit={{ rotateY: dir > 0 ? -70 : 70, opacity: 0 }}
              transition={{ duration: 0.4, ease: 'easeInOut' }}
              className="grid grid-cols-3 gap-2 [transform-style:preserve-3d] sm:gap-3"
            >
              {pageCards.map((card) => {
                const count = ownedMap.get(card.id) || 0;
                return (
                  <JarticardFrame
                    key={card.id}
                    thumb
                    card={card}
                    owned={count > 0}
                    count={count}
                    onClick={count > 0 ? () => setSelected(card) : undefined}
                  />
                );
              })}
              {/* Rellena la hoja para mantener el grid 3x3 */}
              {Array.from({ length: CARDS_PER_PAGE - pageCards.length }).map((_, i) => (
                <div key={`empty-${i}`} className="aspect-[5/7] rounded-xl border border-dashed border-white/5" />
              ))}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* Navegación */}
      <div className="flex items-center justify-center gap-4">
        <Button variant="outline" size="icon" onClick={() => go(-1)} disabled={safePage === 0}>
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <span className="min-w-24 text-center text-sm font-semibold text-muted-foreground">
          Página {safePage + 1} / {totalPages}
        </span>
        <Button variant="outline" size="icon" onClick={() => go(1)} disabled={safePage >= totalPages - 1}>
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      {/* Detalle de carta */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-sm">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {selected.name}
                  <span className="text-xs font-normal text-muted-foreground">#{selected.number} · {JARTICARD_TYPE_LABELS[selected.type]}</span>
                </DialogTitle>
              </DialogHeader>
              <div className="mx-auto w-56">
                <JarticardFrame thumb card={selected} owned count={ownedMap.get(selected.id) || 0} />
              </div>
              <div className="space-y-3">
                <p className="text-center text-sm text-muted-foreground">
                  Tienes <span className="font-bold text-foreground">{ownedMap.get(selected.id) || 0}</span> copia(s).
                </p>
                {(ownedMap.get(selected.id) || 0) >= 2 ? (
                  <Button className="w-full" onClick={() => handleList(selected)} disabled={listing}>
                    {listing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Repeat className="h-4 w-4" />}
                    Poner repetida en el mercado
                  </Button>
                ) : (
                  <p className="text-center text-xs text-muted-foreground">
                    Necesitas al menos 2 copias para poder intercambiar esta carta.
                  </p>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
